import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import {
  Home,
  Building2,
  Laptop,
  Factory,
  Phone,
  Camera,
  Calendar,
  DollarSign,
  Truck,
  Zap,
  Leaf,
  Award,
  CheckCircle2
} from 'lucide-react';

const Services = () => {
  const navigate = useNavigate();

  const headerRef = useRef(null);
  const servicesRef = useRef(null);
  const processRef = useRef(null);
  const benefitsRef = useRef(null);

  const headerInView = useInView(headerRef, { once: true });
  const servicesInView = useInView(servicesRef, { once: true });
  const processInView = useInView(processRef, { once: true });
  const benefitsInView = useInView(benefitsRef, { once: true });

  const services = [
    {
      id: 1,
      title: "Household Scrap Collection",
      description:
        "We collect all types of household scrap including paper, plastic, metal, and glass from your doorstep.",
      features: [
        "Free pickup service",
        "Instant payment",
        "Eco-friendly disposal",
        "Minimum 250Rs pickup value"
      ],
      icon: Home,
      price: "Free Pickup"
    },
    {
      id: 2,
      title: "Business Scrap Management",
      description:
        "Comprehensive scrap management solutions for offices, shops, and commercial establishments.",
      features: [
        "Bulk collection",
        "Regular pickup schedules",
        "Documentation for compliance",
        "Competitive rates"
      ],
      icon: Building2,
      price: "Custom Pricing"
    },
    {
      id: 3,
      title: "E-Waste Collection",
      description:
        "Specialized collection and disposal of electronic waste including computers, phones, and appliances.",
      features: [
        "Data destruction guarantee",
        "Environmentally safe disposal",
        "Certified recycling",
        "Best market rates"
      ],
      icon: Laptop,
      price: "Market Rates"
    },
    {
      id: 4,
      title: "Industrial Scrap",
      description:
        "Large-scale scrap collection for industries, factories, and manufacturing units.",
      features: [
        "Heavy machinery pickup",
        "Industrial-grade equipment",
        "Volume-based pricing",
        "Logistics support"
      ],
      icon: Factory,
      price: "Volume Based"
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Book Online",
      description:
        "Fill out our simple booking form with your details and scrap information",
      icon: Phone
    },
    {
      step: 2,
      title: "Upload Photos",
      description:
        "Share photos of your scrap items for better assessment and pricing",
      icon: Camera
    },
    {
      step: 3,
      title: "Schedule Pickup",
      description:
        "Choose your preferred date and time for scrap collection",
      icon: Calendar
    },
    {
      step: 4,
      title: "Get Paid",
      description:
        "Our collector visits, weighs your scrap, and makes instant payment",
      icon: DollarSign
    }
  ];

  const handleBookNow = () => {
    navigate('/book-appointment');
  };

  return (
    <div className="min-h-screen bg-[#F6F8F4]">

      {/* =====================================================
          HEADER SECTION
      ====================================================== */}

      <section
        ref={headerRef}
        className="section-padding bg-[#FFFFFF]"
      >
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={
              headerInView
                ? { opacity: 1, y: 0 }
                : {}
            }
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >

            <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full bg-[#EAF5EC] border border-[#DDE8DF] text-[#2F6F45] text-sm font-semibold">
              <Leaf className="w-4 h-4 text-[#2F6F45]" />
              SCRAP COLLECTION SOLUTIONS
            </span>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#102018] mb-6">

              Our{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Services
              </span>

            </h1>

            <p className="text-xl text-[#718078] max-w-3xl mx-auto leading-relaxed">
              Comprehensive scrap collection and recycling services for all
              your needs. From households to industries, we provide reliable
              and eco-friendly solutions.
            </p>

          </motion.div>

        </div>
      </section>


      {/* =====================================================
          SERVICES GRID
      ====================================================== */}

      <section
        ref={servicesRef}
        className="section-padding bg-[#F6F8F4]"
      >
        <div className="container-custom">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">

            {services.map((service, index) => {

              const Icon = service.icon;

              return (
                <motion.div
                  key={service.id}
                  className="group relative bg-[#FFFFFF] rounded-2xl border border-[#DDE8DF] p-8 overflow-hidden transition-all duration-300 hover:border-[#70BD7D] hover:shadow-[0_18px_40px_rgba(16,32,24,0.10)]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={
                    servicesInView
                      ? { opacity: 1, y: 0 }
                      : {}
                  }
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  whileHover={{
                    y: -5
                  }}
                >

                  {/* Top Green Line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2F6F45] to-[#70BD7D]"></div>

                  {/* Service Header */}
                  <div className="flex items-start space-x-4 mb-6">

                    <div className="w-16 h-16 rounded-2xl bg-[#EAF5EC] border border-[#DDE8DF] flex items-center justify-center flex-shrink-0 group-hover:bg-[#2F6F45] transition-all duration-300">

                      <Icon
                        className="w-8 h-8 text-[#2F6F45] group-hover:text-[#FFFFFF] transition-colors duration-300"
                        strokeWidth={2}
                      />

                    </div>

                    <div className="flex-1">

                      <h3 className="text-xl font-heading font-semibold text-[#102018] mb-2">
                        {service.title}
                      </h3>

                      <div className="inline-flex items-center px-3 py-1 bg-[#EAF5EC] border border-[#DDE8DF] text-[#2F6F45] text-sm font-semibold rounded-full">
                        {service.price}
                      </div>

                    </div>

                  </div>


                  {/* Description */}
                  <p className="text-[#718078] mb-6 leading-relaxed">
                    {service.description}
                  </p>


                  {/* Features */}
                  <ul className="space-y-3">

                    {service.features.map(
                      (feature, featureIndex) => (

                        <li
                          key={featureIndex}
                          className="flex items-center space-x-3 text-sm text-[#718078]"
                        >

                          <CheckCircle2
                            className="w-5 h-5 text-[#2F6F45] flex-shrink-0"
                            strokeWidth={2}
                          />

                          <span>
                            {feature}
                          </span>

                        </li>

                      )
                    )}

                  </ul>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>


      {/* =====================================================
          HOW IT WORKS
      ====================================================== */}

      <section
        ref={processRef}
        className="section-padding bg-[#FFFFFF]"
      >
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >

            <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full bg-[#EAF5EC] border border-[#DDE8DF] text-[#2F6F45] text-sm font-semibold">
              <Zap className="w-4 h-4 text-[#2F6F45]" />
              SIMPLE PROCESS
            </span>

            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">

              How It{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Works
              </span>

            </h2>

            <p className="text-[#718078] max-w-2xl mx-auto">
              Simple, fast, and reliable process to get your scrap collected
              and get paid instantly
            </p>

          </motion.div>


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

            {processSteps.map((step, index) => {

              const Icon = step.icon;

              return (
                <motion.div
                  key={step.step}
                  className="text-center"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  viewport={{ once: true }}
                >

                  <div className="relative mb-6">

                    {/* Icon */}
                    <div className="w-20 h-20 mx-auto bg-[#EAF5EC] border border-[#DDE8DF] rounded-2xl flex items-center justify-center mb-4">

                      <Icon
                        className="w-10 h-10 text-[#2F6F45]"
                        strokeWidth={2}
                      />

                    </div>


                    {/* Step Number */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#2F6F45] text-[#FFFFFF] rounded-full flex items-center justify-center text-sm font-bold shadow-[0_5px_12px_rgba(47,111,69,0.20)]">
                      {step.step}
                    </div>

                  </div>


                  <h3 className="text-lg font-heading font-semibold text-[#102018] mb-3">
                    {step.title}
                  </h3>

                  <p className="text-[#718078] text-sm leading-relaxed">
                    {step.description}
                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>


      {/* =====================================================
          BENEFITS SECTION
      ====================================================== */}

      <section
        ref={benefitsRef}
        className="section-padding bg-[#F6F8F4]"
      >
        <div className="container-custom">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="text-center mb-16"
          >

            <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full bg-[#EAF5EC] border border-[#DDE8DF] text-[#2F6F45] text-sm font-semibold">
              <Award className="w-4 h-4 text-[#2F6F45]" />
              WHY SCRAPSECTOR
            </span>

            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">

              Why Choose{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                ScrapSector?
              </span>

            </h2>

            <p className="text-[#718078] max-w-2xl mx-auto">
              We're committed to providing the best scrap collection
              experience with unmatched benefits
            </p>

          </motion.div>


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">

            {[
              {
                icon: Truck,
                title: 'Free Pickup',
                description:
                  'We provide free pickup service from your doorstep'
              },
              {
                icon: Zap,
                title: 'Instant Payment',
                description:
                  'Get paid immediately when we collect your scrap'
              },
              {
                icon: Leaf,
                title: 'Eco-Friendly',
                description:
                  'We ensure responsible recycling and disposal'
              },
              {
                icon: Award,
                title: 'Best Rates',
                description:
                  'Competitive prices based on current market rates'
              }
            ].map((benefit, index) => {

              const Icon = benefit.icon;

              return (
                <motion.div
                  key={index}
                  className="group bg-[#FFFFFF] text-center rounded-2xl border border-[#DDE8DF] p-7 hover:border-[#70BD7D] hover:shadow-[0_18px_40px_rgba(16,32,24,0.10)] transition-all duration-300"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  viewport={{
                    once: true
                  }}
                  whileHover={{
                    y: -5
                  }}
                >

                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#EAF5EC] border border-[#DDE8DF] flex items-center justify-center group-hover:bg-[#2F6F45] transition-all duration-300">

                    <Icon
                      className="w-8 h-8 text-[#2F6F45] group-hover:text-[#FFFFFF] transition-colors duration-300"
                      strokeWidth={2}
                    />

                  </div>

                  <h3 className="text-xl font-heading font-semibold text-[#102018] mb-3">
                    {benefit.title}
                  </h3>

                  <p className="text-[#718078] leading-relaxed">
                    {benefit.description}
                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>


      {/* =====================================================
          CTA SECTION
      ====================================================== */}

      <section className="section-padding bg-[#FFFFFF]">

        <div className="container-custom">

          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#102018] via-[#102018] to-[#2F6F45] text-[#FFFFFF]">

            {/* Background Glow */}
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#70BD7D]/15 blur-3xl"></div>

            <div className="absolute -bottom-40 -left-40 w-[450px] h-[450px] rounded-full bg-[#2F6F45]/30 blur-3xl"></div>


            <motion.div
              initial={{
                opacity: 0,
                y: 20
              }}
              whileInView={{
                opacity: 1,
                y: 0
              }}
              transition={{
                duration: 0.8
              }}
              viewport={{
                once: true
              }}
              className="relative z-10 text-center px-6 py-14 md:px-10"
            >

              <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-[#70BD7D]/10 border border-[#70BD7D]/20 flex items-center justify-center">

                <Truck
                  className="w-7 h-7 text-[#70BD7D]"
                  strokeWidth={2}
                />

              </div>

              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                Ready to Sell Your Scrap?
              </h2>

              <p className="text-xl text-[#EAF5EC] mb-8 max-w-2xl mx-auto">
                Join thousands of satisfied customers who trust us with their
                scrap collection needs
              </p>

              <motion.button
                onClick={handleBookNow}
                className="bg-[#70BD7D] text-[#102018] font-semibold text-lg px-8 py-4 rounded-xl shadow-[0_12px_30px_rgba(112,189,125,0.20)] hover:bg-[#FFFFFF] transition-all duration-300"
                whileHover={{
                  scale: 1.05,
                  y: -2
                }}
                whileTap={{
                  scale: 0.95
                }}
              >
                Book Pickup Now
              </motion.button>

            </motion.div>

          </div>

        </div>

      </section>

    </div>
  );
};

export default Services;